/**
 * LERA Premium Slideshow — Phase 5.3+
 * Event-driven autoplay coordination for dual desktop/mobile carousel instances.
 * Uses Dawn SlideshowComponent play()/pause() only; does not replace Dawn autoplay.
 */
(function () {
  const DESKTOP_BREAKPOINT = window.matchMedia('(min-width: 750px)');
  const SWIPE_THRESHOLD = 45;

  /**
   * @returns {NodeListOf<HTMLElement>}
   */
  function getLeraSlideshowComponents() {
    return document.querySelectorAll('slideshow-component.lera-slideshow');
  }

  /**
   * @param {HTMLElement} component
   * @returns {'desktop' | 'mobile' | null}
   */
  function getLeraCarouselScope(component) {
    if (component.classList.contains('small-hide')) return 'desktop';
    if (component.classList.contains('medium-hide')) return 'mobile';
    return null;
  }

  /**
   * @param {HTMLElement} component
   * @returns {boolean}
   */
  function isAutoplayEnabled(component) {
    const slider = component.querySelector('[id^="Slider-"]');
    return Boolean(slider && slider.getAttribute('data-autoplay') === 'true');
  }

  /**
   * Apply merchant loop setting without modifying Dawn global.js.
   * @param {HTMLElement} component
   */
  function applyLeraSlideshowLoopSetting(component) {
    const slider = component.querySelector('[id^="Slider-"]');
    if (!slider) return;
    component.enableSliderLooping = slider.getAttribute('data-loop') !== 'false';
  }

  /**
   * @param {'desktop' | 'mobile'} scope
   * @param {boolean} isDesktopViewport
   * @returns {boolean}
   */
  function shouldAutoplayRun(scope, isDesktopViewport) {
    return (scope === 'desktop' && isDesktopViewport) || (scope === 'mobile' && !isDesktopViewport);
  }

  /**
   * Pause hidden carousel autoplay; resume visible carousel when merchant has not paused.
   */
  function syncLeraSlideshowAutoplay() {
    const isDesktopViewport = DESKTOP_BREAKPOINT.matches;

    getLeraSlideshowComponents().forEach((component) => {
      applyLeraSlideshowLoopSetting(component);

      if (typeof component.pause !== 'function') return;
      if (!isAutoplayEnabled(component)) return;

      const scope = getLeraCarouselScope(component);
      if (!scope) return;

      if (!shouldAutoplayRun(scope, isDesktopViewport)) {
        component.pause();
        return;
      }

      if (component.autoplayButtonIsSetToPlay !== false) {
        component.play();
      }
    });
  }

  /**
   * Restart Dawn autoplay after touch navigation without overriding a merchant pause.
   * @param {HTMLElement} component
   */
  function restartLeraSlideshowAutoplay(component) {
    if (!isAutoplayEnabled(component)) return;
    if (component.autoplayButtonIsSetToPlay === false) return;
    if (typeof component.play !== 'function') return;

    component.play();
  }

  /**
   * Delegate swipe navigation to Dawn's existing arrow controls.
   * @param {HTMLElement} component
   * @param {'next' | 'previous'} direction
   */
  function navigateLeraSlideshow(component, direction) {
    const button = direction === 'next' ? component.nextButton : component.prevButton;
    if (!button || button.disabled) return;

    button.click();
    restartLeraSlideshowAutoplay(component);
  }

  /**
   * Add one-finger horizontal swipe support while preserving native vertical scroll.
   * @param {HTMLElement} component
   */
  function bindLeraSlideshowTouch(component) {
    if (component.dataset.leraSwipeBound === 'true') return;

    const slider = component.querySelector('[id^="Slider-"]');
    if (!slider) return;

    component.dataset.leraSwipeBound = 'true';
    slider.style.touchAction = 'pan-y';

    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let currentY = 0;
    let trackingTouch = false;

    function resetTouch() {
      startX = 0;
      startY = 0;
      currentX = 0;
      currentY = 0;
      trackingTouch = false;
    }

    slider.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) {
          resetTouch();
          return;
        }

        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        currentX = startX;
        currentY = startY;
        trackingTouch = true;
      },
      { passive: true }
    );

    slider.addEventListener(
      'touchmove',
      (event) => {
        if (!trackingTouch || event.touches.length !== 1) return;

        const touch = event.touches[0];
        currentX = touch.clientX;
        currentY = touch.clientY;
      },
      { passive: true }
    );

    slider.addEventListener(
      'touchend',
      (event) => {
        if (!trackingTouch) return;

        if (event.changedTouches.length === 1) {
          currentX = event.changedTouches[0].clientX;
          currentY = event.changedTouches[0].clientY;
        }

        const deltaX = currentX - startX;
        const deltaY = currentY - startY;
        resetTouch();

        if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
        if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

        navigateLeraSlideshow(component, deltaX < 0 ? 'next' : 'previous');
      },
      { passive: true }
    );

    slider.addEventListener('touchcancel', resetTouch, { passive: true });
  }

  let eventsBound = false;

  function isLeraSlideshowSection(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    return Boolean(section?.querySelector('slideshow-component.lera-slideshow'));
  }

  function syncLeraSlideshowAfterSectionLoad(sectionId) {
    if (!sectionId || !isLeraSlideshowSection(sectionId)) return;

    customElements.whenDefined('slideshow-component').then(() => {
      queueMicrotask(initLeraSlideshow);
    });
  }

  function bindLeraSlideshowAutoplayEvents() {
    if (eventsBound) return;
    eventsBound = true;

    if (typeof DESKTOP_BREAKPOINT.addEventListener === 'function') {
      DESKTOP_BREAKPOINT.addEventListener('change', syncLeraSlideshowAutoplay);
    } else {
      DESKTOP_BREAKPOINT.addListener(syncLeraSlideshowAutoplay);
    }

    window.addEventListener('orientationchange', syncLeraSlideshowAutoplay);

    document.addEventListener('shopify:section:load', (event) => {
      syncLeraSlideshowAfterSectionLoad(event.detail?.sectionId);
    });
  }

  function initLeraSlideshow() {
    getLeraSlideshowComponents().forEach((component) => {
      applyLeraSlideshowLoopSetting(component);
      bindLeraSlideshowTouch(component);
    });
    syncLeraSlideshowAutoplay();
    bindLeraSlideshowAutoplayEvents();
  }

  function startWhenReady() {
    customElements.whenDefined('slideshow-component').then(() => {
      queueMicrotask(initLeraSlideshow);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWhenReady, { once: true });
  } else {
    startWhenReady();
  }
})();
